import { createContext, type ReactNode, useContext, useCallback, useEffect, useMemo, useState } from 'react';

import { useCognitoAuth } from '@/contexts/AuthContext';
import { setOrganizationId, api } from '@/lib/api';
import { readOrgCache, writeOrgCache, clearOrgCache } from '@/lib/auth';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';

export interface Organization {
  id: number;
  uuid: string;
  name: string;
  slug: string;
  currency: string;
  timezone: string;
  settings: string;
  is_active: boolean;
  created_at: string;
}

interface OrganizationContextType {
  organization: Organization | null;
  organizations: Organization[];
  loading: boolean;
  error: string | null;
  createOrganization: (data: { name: string; currency?: string; timezone?: string }) => Promise<{ error: Error | null }>;
  switchOrganization: (orgId: number) => void;
  refreshOrganizations: (silent?: boolean) => Promise<void>;
  updateOrganization: (id: number, data: { name?: string; slug?: string; settings?: string }) => Promise<{ error: Error | null }>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading, idToken } = useCognitoAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true); // start true so OnboardingCheck waits for cache/API load
  const [error, setError] = useState<string | null>(null);

  const refreshOrganizations = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await api.get<Organization[]>('/api/organizations', { token: idToken!, errorCode: ErrorCode.FETCH_ORGANIZATIONS });
      setOrganizations(data ?? []);

      const storedOrgId = localStorage.getItem('currentOrganizationId');
      let currentOrg = storedOrgId
        ? data.find((org: Organization) => org.id === Number(storedOrgId))
        : undefined;

      if (!currentOrg && data.length > 0) {
        currentOrg = data[0];
      }

      if (currentOrg) {
        setOrganization(currentOrg);
        setOrganizationId(currentOrg.id);
        localStorage.setItem('currentOrganizationId', String(currentOrg.id));
      }

      setError(null);

      // Cache the result
      if (user?.email) {
        writeOrgCache(user.email, data, currentOrg?.id ?? null);
      }
    } catch (err) {
      setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to load organizations');

      // If unauthorized, clear the stale cache
      if (err instanceof Error && 'code' in err && (err as BeangoError).status === 401) {
        clearOrgCache(user?.email);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user, idToken]);

  const createOrganization = useCallback(async (data: { name: string; currency?: string; timezone?: string }) => {
    if (!idToken) return { error: new Error('Not authenticated') };
    try {
      const newOrg = await api.post<Organization>('/api/organizations', {
        name: data.name,
        currency: data.currency ?? 'CAD',
        timezone: data.timezone ?? 'America/Toronto',
      }, { token: idToken, errorCode: ErrorCode.CREATE_ORGANIZATION });

      const updatedOrgs = [...organizations, newOrg];
      setOrganizations(updatedOrgs);
      setOrganization(newOrg);
      setOrganizationId(newOrg.id);
      localStorage.setItem('currentOrganizationId', String(newOrg.id));

      if (user?.email) {
        writeOrgCache(user.email, updatedOrgs, newOrg.id);
      }

      return { error: null };
    } catch (err) {
      if (err instanceof Error && 'code' in err && (err as BeangoError).status === 401) {
        clearOrgCache(user?.email);
      }
      return { error: err instanceof Error ? err : new Error('Failed to create organization') };
    }
  }, [organizations, user, idToken]);

  const updateOrganization = useCallback(async (id: number, data: { name?: string; slug?: string; settings?: string }) => {
    if (!idToken) return { error: new Error('Not authenticated') };
    try {
      await api.patch(`/api/organizations/${id}`, data, { token: idToken, errorCode: ErrorCode.UPDATE_ORGANIZATION });
      await refreshOrganizations();
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Failed to update organization') };
    }
  }, [refreshOrganizations, idToken]);

  const switchOrganization = useCallback((orgId: number) => {
    const org = organizations.find((o) => o.id === orgId);
    if (org) {
      setOrganization(org);
      setOrganizationId(org.id);
      localStorage.setItem('currentOrganizationId', String(orgId));
      if (user?.email) {
        writeOrgCache(user.email, organizations, org.id);
      }
    }
  }, [organizations, user]);

  const value = useMemo(() => ({
    organization,
    organizations,
    loading,
    error,
    createOrganization,
    updateOrganization,
    refreshOrganizations,
    switchOrganization,
  }), [organization, organizations, loading, error, createOrganization, updateOrganization, refreshOrganizations, switchOrganization]);

  useEffect(() => {
    if (authLoading) {
      // Auth is still resolving — stay in loading state
      setLoading(true);
      return;
    }
    if (user) {
      // Try to initialize from cache first
      const cached = user.email ? readOrgCache(user.email) : null;
      if (cached) {
        setOrganizations(cached.organizations);
        const currentOrg = cached.currentOrgId
          ? cached.organizations.find((o) => o.id === cached.currentOrgId)
          : cached.organizations[0] ?? null;
        if (currentOrg) {
          setOrganization(currentOrg);
          setOrganizationId(currentOrg.id);
          localStorage.setItem('currentOrganizationId', String(currentOrg.id));
        }
        setLoading(false); // Cache hit → orgs are immediately available
        // Always refresh in the background
        void refreshOrganizations(true);
      } else {
        // No cache → blocking refresh so OnboardingCheck waits for orgs
        void refreshOrganizations(false);
      }
    } else {
      setOrganizations([]);
      setOrganization(null);
      setLoading(false);
    }
  }, [authLoading, user, refreshOrganizations]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
